import { Box, createTheme, ThemeProvider } from "@mui/material"
import { Outlet, useLocation } from "react-router-dom"
import Header from "./components/Header/Header"

import { useAuth0 } from "ct-auth"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import RoleAssumptionBanner from "./components/RoleAssumption/RoleAssumptionBanner"
import ErrorBoundary from "./components/common/ErrorBoundary/ErrorBoundary"
import { UiCtButton, UiLayoutContainer } from "./components/common/MuiStyled/LibComponent"
import SidePanel from "./components/common/SidePanel/SidePanel"
import { isTrainer, isManager, isSponsor, userOrg, canManageChildOrgs, canManageOrgs, isEmbedded } from "./data-utils/authUtils"
import { useQueries } from "./data-utils/reactQueryHelpers"
import { resolveEffectiveBrand } from "./data-utils/partnerBranding"
import { DARK_THEME, LIGHT_THEME, withBrandColor } from "./themes/theme-mui"
import { UiActions } from "./store/ui-slice"

/**
 * AppLayout component that serves as the main layout for the application.
 * It includes the Header and an Outlet for nested routes.
 */

function AppLayout() {
  const { user } = useAuth0()
  const dispatch = useDispatch()
  const [currentLocation] = useState(window.location.pathname)
  const location = useLocation()

  const isLeftPanelVisible = useSelector((state) => state.ui.isLeftPanelVisible)
  const isLeftPanelExpanded = useSelector((state) => state.ui.isLeftPanelExpanded)
  const isChromeHidden = useSelector((state) => state.ui.isChromeHidden)
  const isDarkMode = useSelector((state) => state.ui.isDarkMode)

  // White-label accent: drive both the SCSS `$color_theme` (via the
  // --color-theme CSS var) and the MUI primary palette from the effective brand.
  // CT-1163 precedence: the org's OWN brandColor › the inherited managing-partner
  // brandColor › the ChaosTrack default (null → theme keeps its curated gold).
  const { data: orgData } = useQueries.HOME_USER_ORG({ orgId: userOrg(user) })
  const brandColor = resolveEffectiveBrand(orgData).brandColor

  useEffect(() => {
    const root = document.documentElement
    if (brandColor) root.style.setProperty("--color-theme", brandColor)
    else root.style.removeProperty("--color-theme")
  }, [brandColor])

  const brandedTheme = useMemo(() => createTheme(withBrandColor(isDarkMode ? DARK_THEME : LIGHT_THEME, brandColor)), [isDarkMode, brandColor])

  // RBAC
  const [userIsSponsor, setUserIsSponsor] = useState(false) // User is a sponsor role
  const [userIsManager, setUserIsManager] = useState(false) // User is a manager role
  const [userIsTrainer, setUserIsTrainer] = useState(false) // User is an trainer role
  const [userCanManageChildOrgs, setUserCanManageChildOrgs] = useState(false) // Manager or partner-entitled sponsor (exercises gate)
  const [userCanManageOrgs, setUserCanManageOrgs] = useState(false) // Manager or partner-sponsor with organizations:crud (org management gate, CT7)

  useEffect(() => {
    // Maintain user RBAC
    setUserIsManager(isManager(user))
    setUserIsSponsor(isSponsor(user))
    setUserIsTrainer(isTrainer(user))
    setUserCanManageChildOrgs(canManageChildOrgs(user))
    setUserCanManageOrgs(canManageOrgs(user))
  }, [user])

  // Focus-mode routes (e.g. IncidentDetail) flip `isChromeHidden` so the
  // active page can take the whole viewport. We keep the RoleAssumptionBanner
  // visible regardless — impersonation state is a safety affordance the user
  // must always see — but drop the Header, sidepanel, and the layout
  // container's height/margin offsets so the Outlet renders edge-to-edge.
  if (isChromeHidden) {
    return (
      <ThemeProvider theme={brandedTheme}>
        <ErrorBoundary fallback={null}>
          <RoleAssumptionBanner />
        </ErrorBoundary>
        <Box sx={{ height: "100vh", width: "100vw", overflow: "hidden" }}>
          <Outlet />
        </Box>
      </ThemeProvider>
    )
  }

  // In the CTIR app-shell (?embedded=1), the shell provides the header + rail, so we
  // render only the routed content — but still inside UiLayoutContainer with the rail
  // hidden (visible={false}). The .layout-main-content padding/geometry (16px/24px,
  // 100vh, canvas bg, maxWidth) is scoped to that styled container; pages such as
  // Scenarios bleed into that 24px padding with negative margins, so rendering the box
  // bare (no container) drops the padding and the bleed clips content off the edges.
  // Standalone/production is unaffected.
  if (isEmbedded()) {
    return (
      <ThemeProvider theme={brandedTheme}>
        <UiLayoutContainer visible={false} expanded={false}>
          <Box className="layout-main-content">
            <Outlet />
          </Box>
        </UiLayoutContainer>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={brandedTheme}>
      {/* Banner crashes must never bring down the app. Fallback is empty —
          if the banner can't render, the user is mid-session and we
          shouldn't introduce a second source of confusing UI. */}
      <ErrorBoundary fallback={null}>
        <RoleAssumptionBanner />
      </ErrorBoundary>
      <Header currentLocation={currentLocation} />
      <UiLayoutContainer visible={isLeftPanelVisible} expanded={isLeftPanelExpanded}>
        <Box className="layout-sidepanel no-print">
          <SidePanel
            currentLocation={location.pathname}
            userIsManager={userIsManager}
            userIsSponsor={userIsSponsor}
            userIsTrainer={userIsTrainer}
            userCanManageChildOrgs={userCanManageChildOrgs}
            userCanManageOrgs={userCanManageOrgs}
          />
          {/* Button to toggle left panel expansion */}
          <UiCtButton
            variant="contained"
            size="small"
            className="layout-toggle-button"
            onClick={() => dispatch(UiActions.setIsLeftPanelExpanded(!isLeftPanelExpanded))}
          >
            {isLeftPanelExpanded ? <ChevronLeft /> : <ChevronRight />}
          </UiCtButton>
        </Box>
        <Box className="layout-main-content">
          <Outlet />
        </Box>
      </UiLayoutContainer>
    </ThemeProvider>
  )
}

export default AppLayout
